import { fetchAuthSession } from 'aws-amplify/auth';
import {
  blocksFlatsBulkCreateBody,
  buildAmenitiesBulkUpsertBody,
} from './communitySiteUtils';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const RESIDENTS_BASE_URL =
  process.env.REACT_APP_RESIDENTS_OWNERS_API_BASE_URL || API_BASE_URL;

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      return tokens.idToken.toString();
    }
    return null;
  } catch (error) {
    console.error('Error fetching auth session for community onboarding:', error);
    return null;
  }
};

const postJson = async (url, body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  let decoded = {};
  try {
    decoded = await response.json();
  } catch {
    decoded = { raw: await response.text() };
  }

  if (!response.ok) {
    const message =
      decoded.message ||
      decoded.error ||
      `Request failed (${response.status})`;
    throw new Error(message);
  }

  return decoded;
};

export const submitBlocksFlatsBulk = async (siteId, siteData) => {
  const url = `${API_BASE_URL.replace(/\/+$/, '')}/facility/sites/${siteId}/blocks-flats/bulk-create`;
  return postJson(url, blocksFlatsBulkCreateBody(siteData));
};

export const bulkUpsertAmenities = async (siteId, amenities) => {
  const base = RESIDENTS_BASE_URL.replace(/\/+$/, '');
  const url = `${base}/sites/${siteId}/amenities/bulk-upsert`;
  return postJson(url, buildAmenitiesBulkUpsertBody(amenities));
};

export const submitFullOnboarding = async (siteId, siteData, amenities, onProgress) => {
  if (!siteId?.trim()) {
    return {
      success: false,
      message: 'No site selected. Choose a site before converting to a community.',
    };
  }

  onProgress?.({ step: 'blocks', status: 'in_progress' });

  let blocksResponse;
  try {
    blocksResponse = await submitBlocksFlatsBulk(siteId, siteData);
    onProgress?.({ step: 'blocks', status: 'completed' });
  } catch (error) {
    onProgress?.({ step: 'blocks', status: 'failed', error: error.message });
    throw error;
  }

  onProgress?.({ step: 'amenities', status: 'in_progress' });

  try {
    const amenitiesResponse = await bulkUpsertAmenities(siteId, amenities);
    onProgress?.({ step: 'amenities', status: 'completed' });
    const message =
      amenitiesResponse.message ||
      blocksResponse.message ||
      'Site structure and amenities saved.';
    return {
      success: true,
      message,
      blocksResponse,
      amenitiesResponse,
    };
  } catch (error) {
    onProgress?.({ step: 'amenities', status: 'failed', error: error.message });
    throw error;
  }
};
